import { useEffect, useState, useMemo } from 'react'
import { ReactComponent as IconBack } from "assets/images/icons/project-icons/arrow_back.svg"
import { ReactComponent as IconTrash } from "assets/images/icons/project-icons/trash-2.svg"
import { ReactComponent as IconEarnipayRounded } from "assets/images/icons/project-icons/earnipay-logo-rounded.svg"
import FormDrawer from 'components/__new/common/form-drawer'
import CustomRadioButton from 'components/__new/common/CustomRadioButton'
import CurrencyFormat, { CurrencyFormatWithoutSymbol } from 'utils/currencyFormat'
import Maybe from 'components/__new/common/Maybe'
import { toast } from 'react-toastify'
import useWarningMessage from 'hooks/useWarningMessage'
import { useSelector } from 'react-redux'



const getAmountFromCurrencyAmount = (value) => {
    return value?.split(",")?.join("");
}

const getAmountWithDecimals = (value) => {
    if (value === "0") return;
    const cleanedValue = value.replace(/[^\d.]/g, '');
    const [integerPart, decimalPart] = cleanedValue.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    let formattedValue = formattedInteger;
    if (decimalPart !== undefined) {
        formattedValue += '.' + decimalPart.slice(0, 2);
    }
    return formattedValue;
}

const EarnipayMultipleTransferAmountSetup = ({
    isOpen,
    toggleEarnipayTransferType,
    toggleMultipleEarnipayTransferAmount,
    earnipayPreviewItems,
    setEarnipayPreviewItems,
    initiateTransfer,
    selectedSourceAccount, 
    setSelectedSourceAccount
}) => {
    const {multipleSourceWalletId} = useSelector(s => s.entities.wallet);
    
    const [inputsDisabled, setInputsDisabled] = useState(false);
    const [totalFees, setTotalFees] = useState(0);
    const [subTotal, setSubTotal] = useState(0);
    const { warningShown, WarningMessage } = useWarningMessage();
    
    const [form, setForm] = useState({
        amountType: "same",
        description: "",
        amount: "",
        walletId: "",
    });    
    
    const isBalanceInsufficient = useMemo(() => {
        const amount = form?.amount?.split(",")?.join("");
        const fullAmount = form?.amountType === "same" ? +amount * earnipayPreviewItems?.length : +amount;
        return fullAmount > (selectedSourceAccount?.balance / 100)
    }, [form?.amount, form?.amountType, selectedSourceAccount]);
    
    const isBalanceInsufficientDifferent = useMemo(() => {
        const totalAmount = earnipayPreviewItems?.reduce((acc, cur) => {
            const totalCurr = acc + Number(getAmountFromCurrencyAmount(cur.amount?.toString()));
            return totalCurr;
        }, 0);
        return totalAmount > (selectedSourceAccount?.balance / 100)
    }, [earnipayPreviewItems, selectedSourceAccount]);
    
    const removePreviewItem = (id) => {
        const filtered = earnipayPreviewItems?.filter((item) => item?.accountNumber !== id);
        setEarnipayPreviewItems(filtered);
    }
    
    const getTransferFee = (amount) => {
        if (amount >= 10 && amount < 5001) {
            return 12
        } else if (amount > 5000 && amount < 50001) {
            return 30
        } else if (amount > 50000) {
            return 60
        }
        return 0
    }
    
    const amountProps = [
        {
            title: "Set Same Amount",
            value: "same",
        },
        {
            title: "Set Different Amount",
            value: "different",
        },
        {
            title: "Split an amount equally",
            value: "split"
        }
    ]
    
    const updateAmount = (accountNumber, amount) => {
        if (amount === "0") return;
        const cleanedValue = amount.replace(/[^\d.]/g, '');
        const [integerPart, decimalPart] = cleanedValue.split('.');
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        let formattedValue = formattedInteger;
        if (decimalPart !== undefined) {
            formattedValue += '.' + decimalPart.slice(0, 2);
        }
        const updateEntries = earnipayPreviewItems?.map(
            (item) => item.accountNumber === accountNumber ? ({
                ...item, 
                amount: formattedValue, 
                fee: getTransferFee(getAmountFromCurrencyAmount(formattedValue)) 
            }) : item
        )
        
        setEarnipayPreviewItems(updateEntries)
    }
    
    const returnToPrevious = () => { 
        toggleMultipleEarnipayTransferAmount();
        toggleEarnipayTransferType();
    }
    
    const onProceed = () => {
        const invalidMinAmount = earnipayPreviewItems?.find((item) => +item?.amount < 50)
        
        if(invalidMinAmount) {
            toast.error("Each beneficiary must have a minimum amount of ₦50")
        } else {
            const recipients = earnipayPreviewItems?.map(({amount, accountNumber}) => ({
                amount: Number(amount?.split(",").join("")),
                accountNumber: accountNumber.toString(),
                walletId: Number(multipleSourceWalletId)
            }))
            
            const transferPayload = {
                recipients,
                description: form.description
            }
            
            toggleMultipleEarnipayTransferAmount();
            initiateTransfer(transferPayload);
        }
    }
    
    useEffect(() => {
        if(form.amountType === 'same') {
            setTotalFees(
                getTransferFee(
                    getAmountFromCurrencyAmount(form.amount)
                ) * earnipayPreviewItems?.length
            );
            setSubTotal(getAmountFromCurrencyAmount(form?.amount) * earnipayPreviewItems.length);
        } else if(form.amountType === 'split') {
            setTotalFees(
                getTransferFee(
                    getAmountFromCurrencyAmount(form.amount)
                ) * earnipayPreviewItems?.length
            );
            setSubTotal(getAmountFromCurrencyAmount(form?.amount));
            setEarnipayPreviewItems([
                ...earnipayPreviewItems?.map((item) => ({
                    ...item, 
                    amount: (getAmountFromCurrencyAmount(form.amount) / earnipayPreviewItems?.length), 
                    fee: 0,
                }))
            ]);
        } 
        // eslint-disable-next-line
    }, [form.amountType, form.amount])
    
    useEffect(() => {
        if(form?.amountType === 'different') {
            setTotalFees(earnipayPreviewItems?.reduce((acc, cur) => {
                const currVal = +cur.fee;
                const totalCur = acc + currVal;
                return totalCur;
            }, 0));
            setSubTotal(earnipayPreviewItems?.reduce((acc, cur) => {
                const totalCurr = acc + Number(getAmountFromCurrencyAmount(cur.amount));
                return totalCurr;
            }, 0));
        }
        // eslint-disable-next-line
    }, [earnipayPreviewItems])
    
    useEffect(() => {
        if(form.amountType === "same") {
            setInputsDisabled(true);
            setEarnipayPreviewItems([
                ...earnipayPreviewItems?.map((item) => ({
                    ...item, 
                    amount: getAmountFromCurrencyAmount(form.amount),
                    fee: 0
                }))
            ]);
        } else if (form.amountType === "different") {
            setInputsDisabled(false);
            setForm({...form, amount: ""})
            setEarnipayPreviewItems([...earnipayPreviewItems?.map((item) => ({...item, amount: "", fee: 0}))])
        } else if (form.amountType === "split") {
            setInputsDisabled(true);
        }
        // eslint-disable-next-line
    }, [form.amountType, form.amount])
    
    useEffect(() => {
        setTotalFees(0)
        setSubTotal(0)
        setForm({
            amountType: "same",
            description: "",
            amount: "",
        });
        setEarnipayPreviewItems([...earnipayPreviewItems?.map((item) => ({...item, amount: "", fee: 0}))]);
        // eslint-disable-next-line
    }, [])
    
    return (
        <FormDrawer display="center" size="3xl" isOpen={isOpen} setIsOpen={() => {}}>
            <div className='p-7'>
                <div className='flex items-center'>
                    <div className="w-7 h-7 flex items-center justify-center mr-3 bg-[#f4f4f4] rounded-md">
                        <IconBack
                            className='cursor-pointer'
                            onClick={returnToPrevious}
                        />
                    </div>
                    <div className="font-semibold lg:text-lg">Set Amount</div>
                </div>
                
                <div className="w-full flex items-center justify-items-center lg:items-start lg:justify-between flex-wrap lg:flex-nowrap mt-7">
                    <div className='w-full lg:w-3/5'>
                        <div className='mb-3.5 font-medium'>Set amount to transfer</div>
                        {amountProps.map((item, index) => (
                            <div key={index} onClick={() => setForm({...form, amountType: item.value})} className="mb-2.5 w-max flex items-center cursor-pointer">
                                <CustomRadioButton
                                    widthHeight={5}
                                    checked={form.amountType === item.value}
                                />
                                <div className="ml-2 text-ep-primary-light-200">{item.title}</div>
                            </div>
                        ))}
                        
                        <Maybe condition={form.amountType === "same" || form?.amountType === "split"}>
                            <div className='mt-6'>
                                <label className='text-sm'>Amount (Naira)</label>
                                <input
                                    type="string"
                                    id="prl-input"
                                    placeholder="0.00"
                                    value={form.amount}
                                    className="form-input !h-8 !rounded-lg"
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        if (value === "0") return;
                                        const cleanedValue = value.replace(/[^\d.]/g, '');
                                        const [integerPart, decimalPart] = cleanedValue.split('.');
                                        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        let formattedValue = formattedInteger;
                                        if (decimalPart !== undefined) {
                                            formattedValue += '.' + decimalPart.slice(0, 2);
                                        }
                                        setForm({
                                            ...form,
                                            amount:formattedValue
                                        })
                                    }}
                                />
                            </div>
                            {
                                isBalanceInsufficient && (
                                    <div className="text-sm text-ep-danger-200 mt-1">
                                        You don't have sufficient balance to complete this transfer
                                    </div>
                                )
                            }
                        </Maybe>
                    </div>
                    
                    <div className='w-full lg:w-2/5 mt-5 lg:mt-0'>
                        <p className="text-sm mb-1 text-[#121619]">Narration</p>
                        <textarea 
                            rows="4"
                            style={{resize: "none"}}
                            placeholder='Add Transaction Notes here...'
                            onChange={(e) => setForm({...form, description: e.target.value})}
                            className="form-input-textarea !rounded-lg placeholder-[#9ca3af]"
                        />
                    </div>
                </div>
                
                <div className="w-full border-t mt-7 pt-4">
                    <div className="w-full pt-3">
                        {earnipayPreviewItems?.map((item, index) => {
                            return (
                                <div className="mb-7 lg:mb-3 flex items-center justify-center lg:items-center lg:justify-start flex-wrap lg:flex-nowrap" key={index}>
                                    
                                    <div className="flex items-center w-full p-2 border border-gray-200 rounded-lg">
                                        <IconEarnipayRounded />
                                        
                                        <div className='ml-4'>
                                            <p className="text-ep-gray-600 capitalize">{item?.accountName}</p>
                                            <div className="flex items-center text-ep-gray-500">
                                                <span className='mb-1'>Earnipay</span>
                                                <span className='h-1 w-1 rounded-full bg-[#d1d5db] mx-1.5' />
                                                <p>{item?.accountNumber}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className='flex items-end !justify-start w-full lg:w-2/5 ml-8'>
                                        <div>
                                            <label className='text-sm'>Amount (Naira)</label>
                                            <input
                                                type="string"
                                                id="prl-input"
                                                placeholder="0.00"
                                                value={
                                                    form?.amountType === 'split' ? (
                                                        getAmountWithDecimals(
                                                            String(
                                                                Number(
                                                                    getAmountFromCurrencyAmount(form?.amount)
                                                                ) / earnipayPreviewItems?.length
                                                            )
                                                        )
                                                    ) 
                                                    : form?.amountType === 'same' ? (
                                                        form?.amount?.toString() 
                                                    )
                                                    : item?.amount?.toString()
                                                }
                                                disabled={inputsDisabled}
                                                className="form-input !h-8 !rounded-lg"
                                                onChange={(e) => {
                                                    updateAmount(
                                                        item?.accountNumber, 
                                                        e.target.value
                                                    );
                                                }}
                                            />
                                        </div>
                                    
                                        <button className='ml-3 lg:pb-px' onClick={() => removePreviewItem(item?.accountNumber)}>
                                            <IconTrash />
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                
                <div className="rounded-xl py-1.5 px-3 w-full bg-ep-primary-light-100">
                    <div className="flex items-center justify-between w-full">
                        <p className="text-ep-gray-500 text-sm">Recipients</p>
                        <p className="text-ep-gray-600">{earnipayPreviewItems?.length}</p>
                    </div>
                    <div className="flex items-center justify-between w-full">
                        <p className="text-ep-gray-500 text-sm">Fees</p>
                        <p className="text-ep-gray-600">{totalFees}</p>
                    </div>
                    <div className="flex items-center justify-between w-full mt-1 border-t border-dashed pt-1">
                        <p className="text-ep-gray-600">Subtotal</p>
                        <p className="text-ep-gray-600">{CurrencyFormat(subTotal || 0)}</p>
                    </div>
                </div>
                {
                    selectedSourceAccount && (
                        <div className='flex text-sm text-ep-primary-600 mt-2 ml-1'>
                            <p>Balance:</p>&nbsp;
                            <p>
                                {CurrencyFormat(selectedSourceAccount?.balance / 100)}
                            </p>
                        </div>
                    )
                }
                {
                    (form?.amountType === "different" && isBalanceInsufficientDifferent) && (  
                        <div className="text-sm text-ep-danger-200 mt-1">
                            You don't have sufficient balance to complete this transfer
                        </div>
                    )
                }
                <WarningMessage
                    data={earnipayPreviewItems}
                    dependencies={[earnipayPreviewItems, form.amount]}
                />
                
                <div className="w-full flex items-center justify-end pt-8">
                    <button type="button" onClick={returnToPrevious} className="btn btn-transparent-primary mr-3">
                        Cancel
                    </button>
                    
                    <button onClick={onProceed} disabled={warningShown || (form?.amountType === "different" && isBalanceInsufficientDifferent) || (form?.amountType !== "different" && isBalanceInsufficient)} type="button" className="btn btn-ep-primary">
                        Continue
                    </button>
                </div>
            </div>
        </FormDrawer>
        
    )
}


export default EarnipayMultipleTransferAmountSetup